import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Button, Col, Form, Radio, Row, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import { GlobalTable } from "../../../global/components/GlobalTable";
import useGet from "../../../global/hooks/useGet";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import CustomSwitch from "../../../global/components/inputWidgets/CustomSwitch";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { getDealerNo, getOemNo } from "../../../global/hooks/getUserRole";
import { getOptionLabel, termOptions } from "../../../utils/Common";

const initialFilterState = {
  Item: undefined,
  CodeNo: undefined,
  Term: undefined,
};

const FILTER_PARAM_KEYS = ["Item", "CodeNo", "Term"];

const isUsedRow = (record) =>
  record?.Used === 1 ||
  record?.Used === "1" ||
  record?.Used === true ||
  record?.Used === "Yes";

const getRowKey = (record, index = 0) =>
  record?.ItemNo ?? record?.id ?? record?.key ?? index;

const formatDateTime = (value) => {
  if (!value) return null;
  return dayjs(value)
    .toDate()
    .toISOString()
    .replace(/(\.\d{3})Z$/, "$1000Z");
};

/** Term values are months (0 = Lifetime → no EndTime). */
const calcEndTime = (start, term) => {
  if (!start) return null;
  const months = Number(term);
  if (!Number.isFinite(months) || months <= 0) return null;
  return dayjs(start).add(months, "month");
};

const isLifetimeTerm = (term) => {
  const months = Number(term);
  return !Number.isFinite(months) || months <= 0;
};

const renewalFromApi = (row) => Number(row?.Renewal) === 1;

const getItemTypeKey = (record) => {
  if (record?.Item != null && record.Item !== "") return String(record.Item);
  if (record?.ItemName != null && record.ItemName !== "") {
    return String(record.ItemName);
  }
  return null;
};

const getItemTypeLabel = (record) =>
  record?.ItemName ||
  (record?.Item != null ? `Item ${record.Item}` : "this item");

const getPrepurchasedColumns = ({
  selectedKeys,
  handleSelectChange,
  getDisabledReason,
  form,
  showRenewal,
}) => {
  const columns = [
    {
      title: "Item",
      dataIndex: "ItemName",
      key: "ItemName",
      width: 140,
      ellipsis: true,
      render: (_, record) => record?.ItemName || record?.Item || "",
    },
    {
      title: "Code",
      dataIndex: "CodeName",
      key: "CodeName",
      width: 130,
      ellipsis: true,
      render: (_, record) => record?.CodeName || record?.Code || "",
    },
    {
      title: "Term",
      dataIndex: "Term",
      key: "Term",
      width: 100,
      render: (value) => getOptionLabel(termOptions, value) || value || "",
    },
    {
      title: "Start Time",
      key: "StartTime",
      width: 150,
      render: (_, record) => {
        const key = String(getRowKey(record));
        const isSelected = selectedKeys.includes(getRowKey(record));
        return (
          <div className="prepurchased-row-control">
            <CustomDateTimePicker
              name={["edits", key, "StartTime"]}
              format="YYYY-MM-DD HH:mm"
              size="small"
              disablePast
              rules={
                isSelected
                  ? [{ required: true, message: "Start Time is required" }]
                  : []
              }
              onChange={(value) => {
                form.setFieldValue(
                  ["edits", key, "EndTime"],
                  calcEndTime(value, record?.Term),
                );
                if (isSelected) {
                  form.validateFields([["edits", key, "StartTime"]]).catch(
                    () => {},
                  );
                }
              }}
            />
          </div>
        );
      },
    },
    {
      title: "End Time",
      key: "EndTime",
      width: 150,
      render: (_, record) => {
        if (isLifetimeTerm(record?.Term)) {
          return "";
        }
        const key = String(getRowKey(record));
        return (
          <div className="prepurchased-row-control">
            <CustomDateTimePicker
              name={["edits", key, "EndTime"]}
              format="YYYY-MM-DD HH:mm"
              size="small"
              disabled
            />
          </div>
        );
      },
    },
  ];

  if (showRenewal) {
    columns.push({
      title: "Renewal",
      key: "Renewal",
      width: 80,
      align: "center",
      render: (_, record) => {
        if (isLifetimeTerm(record?.Term)) {
          return "";
        }
        const key = String(getRowKey(record));
        return (
          <div className="prepurchased-row-control flex justify-center">
            <CustomSwitch
              name={["edits", key, "Renewal"]}
              size="small"
            />
          </div>
        );
      },
    });
  }

  columns.push({
    title: "",
    key: "select",
    width: 44,
    align: "center",
    render: (_, record) => {
      if (isUsedRow(record)) return null;
      const disabledReason = getDisabledReason(record);
      return (
        <Tooltip title={disabledReason || ""}>
          {/* span keeps the tooltip working while the radio is disabled */}
          <span className="inline-flex">
            <Radio
              checked={selectedKeys.includes(getRowKey(record))}
              disabled={Boolean(disabledReason)}
              onClick={(e) => {
                e.preventDefault();
                handleSelectChange(record);
              }}
            />
          </span>
        </Tooltip>
      );
    },
  });

  return columns;
};

export const PrepurchasedItemsForm = forwardRef(
  ({ dealerNo, multiSelect = false }, ref) => {
    const [form] = Form.useForm();

    const loggedInDealerNo = getDealerNo();
    const loggedInOemNo = getOemNo();
    const loggedInDealerListUrl =
      loggedInDealerNo != null && loggedInOemNo != null
        ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${loggedInOemNo}/dealers/`
        : null;
    const { data: loggedInDealerList } = useGet(loggedInDealerListUrl);
    const showRenewal = useMemo(() => {
      const dealer = (loggedInDealerList?.data ?? []).find(
        (d) => Number(d.value) === Number(loggedInDealerNo),
      );
      return Number(dealer?.Renewal ?? dealer?.renewal) === 1;
    }, [loggedInDealerList, loggedInDealerNo]);
    const [listData, setListData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [filter, setFilter] = useState(initialFilterState);
    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const [params, setParams] = useState({
      limit: 10,
      page: 1,
      sort_by: "ItemNo",
      order: "desc",
    });

    const queryObj = {
      ...params,
      ...(dealerNo != null && dealerNo !== "" ? { DealerNo: dealerNo } : {}),
    };
    const query = new URLSearchParams(queryObj).toString();
    const url =
      dealerNo != null && dealerNo !== ""
        ? `${process.env.REACT_APP_API_URL}/api/devices/pre-purchased-items/?${query}`
        : null;

    const { data, loading } = useGet(url);

    const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
    const { data: optionData, loading: optionDataLoading } = useGet(optionUrl);
    const modifiedItems = useMemo(
      () => getModifiedResponse(optionData, "items"),
      [optionData],
    );

    const codesUrl =
      filter.Item != null && filter.Item !== ""
        ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&Item=${filter.Item}`
        : null;
    const { data: codesData, loading: codesLoading } = useGet(codesUrl);
    const codeOptions = useMemo(
      () =>
        (codesData?.data ?? []).map((item) => ({
          label: item.CodeName || String(item.Code),
          value: item.CodeNo,
        })),
      [codesData],
    );

    const paginationConfig = {
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: data?.count ?? data?.total ?? pagination.total,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      showTotal: (total, range) =>
        `Total ${total} items ${range[0]}-${range[1]}`,
      onChange: (page, pageSize) => {
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: pageSize,
        }));
        setParams((prev) => ({
          ...prev,
          limit: pageSize,
          page: page,
        }));
      },
    };

    const selectedKeys = selectedRows.map((row) => getRowKey(row));

    const buildSelectedPayload = () => {
      const edits = form.getFieldValue("edits") || {};
      return selectedRows.map((row) => {
        const key = String(getRowKey(row));
        const rowEdit = edits[key] || {};
        const start = rowEdit.StartTime;
        const isLifetime = isLifetimeTerm(row?.Term);
        const end = isLifetime
          ? null
          : (rowEdit.EndTime ?? calcEndTime(start, row?.Term));
        return {
          ...row,
          ItemName: row.ItemName ?? row.Item ?? null,
          CodeName: row.CodeName ?? row.Code ?? null,
          StartTime: formatDateTime(start),
          EndTime: formatDateTime(end),
          Renewal:
            !isLifetime && showRenewal && rowEdit.Renewal ? 1 : 0,
        };
      });
    };

    useImperativeHandle(ref, () => ({
      getSelectedRows: () => buildSelectedPayload(),
      validateSelected: async () => {
        if (!selectedRows.length) return false;
        const fields = selectedRows.map((row) => [
          "edits",
          String(getRowKey(row)),
          "StartTime",
        ]);
        try {
          await form.validateFields(fields);
          return true;
        } catch {
          return false;
        }
      },
    }));

    // Multi-select allows one row per item type.
    const getDisabledReason = (record) => {
      if (!multiSelect) return null;
      const key = getRowKey(record);
      const itemTypeKey = getItemTypeKey(record);
      if (itemTypeKey == null) return null;
      const otherSelected = selectedRows.some(
        (row) =>
          getRowKey(row) !== key && getItemTypeKey(row) === itemTypeKey,
      );
      return otherSelected
        ? `Only one ${getItemTypeLabel(record)} item can be selected. Unselect the other one first.`
        : null;
    };

    const handleSelectChange = (record) => {
      const key = getRowKey(record);
      if (multiSelect) {
        const exists = selectedRows.some((row) => getRowKey(row) === key);
        if (exists) {
          setSelectedRows((prev) =>
            prev.filter((row) => getRowKey(row) !== key),
          );
          form.setFields([
            {
              name: ["edits", String(key), "StartTime"],
              errors: [],
            },
          ]);
          return;
        }
        if (getDisabledReason(record)) return;

        const now = dayjs();
        const editKey = String(key);
        form.setFieldsValue({
          edits: {
            ...form.getFieldValue("edits"),
            [editKey]: {
              ...(form.getFieldValue(["edits", editKey]) || {}),
              StartTime: now,
              EndTime: calcEndTime(now, record?.Term),
            },
          },
        });
        setSelectedRows((prev) => [...prev, record]);
        setTimeout(() => {
          form
            .validateFields([["edits", editKey, "StartTime"]])
            .catch(() => {});
        }, 0);
        return;
      }
      const now = dayjs();
      const editKey = String(key);
      form.setFieldsValue({
        edits: {
          ...form.getFieldValue("edits"),
          [editKey]: {
            ...(form.getFieldValue(["edits", editKey]) || {}),
            StartTime: now,
            EndTime: calcEndTime(now, record?.Term),
          },
        },
      });
      setSelectedRows([record]);
      setTimeout(() => {
        form
          .validateFields([["edits", editKey, "StartTime"]])
          .catch(() => {});
      }, 0);
    };

    useEffect(() => {
      if (data && !loading) {
        const newData = (data?.data ?? []).map((el, index) => ({
          ...el,
          key: getRowKey(el, index),
        }));
        setListData(newData);

        const prevEdits = form.getFieldValue("edits") || {};
        const nextEdits = { ...prevEdits };
        const now = dayjs();
        newData.forEach((row) => {
          const key = String(getRowKey(row));
          if (nextEdits[key]) return;
          nextEdits[key] = {
            StartTime: now,
            EndTime: calcEndTime(now, row?.Term),
            Renewal:
              showRenewal && !isLifetimeTerm(row?.Term) && renewalFromApi(row),
          };
        });
        form.setFieldsValue({ edits: nextEdits });

        const totalCount = data?.count ?? data?.total;
        if (typeof totalCount === "number") {
          setPagination((prev) => ({ ...prev, total: totalCount }));
        }
      }
    }, [data, loading, form, showRenewal]);

    useEffect(() => {
      const edits = form.getFieldValue("edits") || {};
      const nextEdits = { ...edits };
      listData.forEach((row) => {
        const key = String(getRowKey(row));
        nextEdits[key] = {
          ...(nextEdits[key] || {}),
          Renewal:
            showRenewal && !isLifetimeTerm(row?.Term) && renewalFromApi(row),
        };
      });
      form.setFieldsValue({ edits: nextEdits });
    }, [showRenewal]);

    const handleFilter = () => {
      const filterDict = {};
      for (const key of FILTER_PARAM_KEYS) {
        if (
          filter[key] !== null &&
          filter[key] !== "" &&
          filter[key] !== undefined
        ) {
          filterDict[key] = filter[key];
        }
      }
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams((prev) => {
        const next = { ...prev, page: 1 };
        FILTER_PARAM_KEYS.forEach((key) => {
          delete next[key];
        });
        return { ...next, ...filterDict };
      });
    };

    const handleReset = () => {
      setFilter(initialFilterState);
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0,
      });
      setParams({
        limit: 10,
        page: 1,
        sort_by: "ItemNo",
        order: "desc",
      });
      setSelectedRows([]);
      form.resetFields();
    };

    if (dealerNo == null || dealerNo === "") {
      return (
        <div className="py-4 text-gray-500">
          No dealer context. Please ensure you are logged in as a dealer.
        </div>
      );
    }

    return (
      <Form
        form={form}
        layout="vertical"
        className="mt-3 prepurchased-items-form"
      >
        <div className="mt-3">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                allowClear
                size="small"
                placeholder="Item"
                optionFilterProp="label"
                options={modifiedItems}
                loading={optionDataLoading}
                value={
                  filter.Item != null && filter.Item !== ""
                    ? Number(filter.Item)
                    : undefined
                }
                onChange={(value) =>
                  setFilter((prev) => ({
                    ...prev,
                    Item: value,
                    CodeNo: undefined,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                allowClear
                size="small"
                placeholder="Code"
                optionFilterProp="label"
                options={codeOptions}
                loading={codesLoading}
                disabled={filter.Item == null || filter.Item === ""}
                value={filter.CodeNo}
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, CodeNo: value }))
                }
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                allowClear
                size="small"
                placeholder="Term"
                optionFilterProp="label"
                options={termOptions}
                value={
                  filter.Term != null && filter.Term !== ""
                    ? Number(filter.Term)
                    : undefined
                }
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, Term: value }))
                }
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </div>

        <div className="flex items-center my-4 flex-nowrap gap-2">
          <Button
            type="primary"
            className="mr-2 w-[104px] h-[30px] font-medium"
            onClick={handleFilter}
          >
            <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
            Apply
          </Button>
          <Button
            className="reset-button w-[104px] h-[30px] font-medium"
            onClick={handleReset}
          >
            <HiArrowPath className="!text-[14px] font-medium" />
            Reset
          </Button>
        </div>

        <GlobalTable
          columns={getPrepurchasedColumns({
            selectedKeys,
            handleSelectChange,
            getDisabledReason,
            form,
            showRenewal,
          })}
          dataSource={listData}
          pagination={paginationConfig}
          loading={loading}
        />
      </Form>
    );
  },
);
